import React, { useState, useEffect } from 'react';
import { addDoc, onSnapshot, orderBy, query, serverTimestamp, updateDoc } from 'firebase/firestore';
import { motion, AnimatePresence } from 'framer-motion';
import { nightNoteDoc, nightNotesColl, petDoc } from '../db';
import { queueNotification } from '../hooks/usePushNotification';
import { UserProfile, Screen } from '../App';
import { recordActivity } from '../utils/activityFeed';

interface Props { user: UserProfile; navigate: (s: Screen) => void; backTo?: Screen; }

interface Note {
  id: string;
  text: string;
  userId: string;
  userName: string;
  date: string;
  timestamp: any;
}

function getToday() {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
}

function formatDate(dateStr: string) {
  const d = new Date(dateStr + 'T00:00:00');
  return d.toLocaleDateString('tr-TR', { day: 'numeric', month: 'long', year: 'numeric' });
}

export default function NightNote({ user, navigate, backTo = 'memories' }: Props) {
  const [notes, setNotes] = useState<Note[]>([]);
  const [text, setText] = useState('');
  const [saving, setSaving] = useState(false);
  const [editing, setEditing] = useState(false);
  const [flyAnim, setFlyAnim] = useState(false);
  const today = getToday();

  useEffect(() => {
    const q = query(nightNotesColl(user.coupleId), orderBy('timestamp', 'desc'));
    return onSnapshot(q, snap => {
      setNotes(snap.docs.map(d => ({ id: d.id, ...d.data() } as Note)));
    });
  }, [user.coupleId]);

  const partner = user.displayName === 'Serhat' ? 'Ayça' : 'Serhat';
  const todayNotes = notes.filter(n => n.date === today);
  const todayMyNote = todayNotes.find(n => n.userId === user.uid);
  const todayPartnerNote = todayNotes.find(n => n.userId !== user.uid);

  const save = async () => {
    const nextText = text.trim();
    if (!nextText) return;
    setSaving(true);
    try {
      if (editing && todayMyNote) {
        await updateDoc(nightNoteDoc(user.coupleId, todayMyNote.id), {
          text: nextText,
          updatedAt: serverTimestamp(),
        });
        recordActivity(user, {
          type: 'night_note',
          title: 'Gece notu güncellendi',
          body: nextText.slice(0, 140),
          dayKey: today,
          sourceId: user.uid,
        }).catch(() => {});
        setEditing(false);
        setText('');
        return;
      }

      await addDoc(nightNotesColl(user.coupleId), {
        text: nextText,
        userId: user.uid,
        userName: user.displayName,
        date: today,
        timestamp: serverTimestamp(),
      });
      recordActivity(user, {
        type: 'night_note',
        title: 'Gece notu bırakıldı',
        body: nextText.slice(0, 140),
        dayKey: today,
        sourceId: user.uid,
      }).catch(() => {});
      updateDoc(petDoc(user.coupleId), {
        hunger: 100,
        happiness: 85,
        lastFed: serverTimestamp(),
        lastActive: serverTimestamp(),
      }).catch(() => {});
      if (user.partnerUid) {
        queueNotification(
          user.coupleId,
          user.partnerUid,
          `${user.displayName} bu geceye bir cümle bıraktı`,
          'Seninkiyle yan yana duracak.',
          'night-note',
        );
      }
      setFlyAnim(true);
      setTimeout(() => { setFlyAnim(false); setText(''); }, 700);
    } finally {
      setSaving(false);
    }
  };

  const startEdit = () => {
    if (!todayMyNote) return;
    setText(todayMyNote.text);
    setEditing(true);
  };

  const cancelEdit = () => {
    setEditing(false);
    setText('');
  };

  // Group past notes by date
  const grouped: Record<string, Note[]> = {};
  notes.filter(n => n.date !== today).forEach(n => {
    if (!grouped[n.date]) grouped[n.date] = [];
    grouped[n.date].push(n);
  });

  return (
    <div>
      <div className="page-header">
        <button className="back-btn" onClick={() => navigate(backTo)}>←</button>
        <h1 style={styles.title}>Gece Notu</h1>
      </div>

      <div style={styles.body}>
        <section style={styles.heroCard}>
          <div style={styles.heroMeta}>
            <span>Bu gece</span>
            <span>{formatDate(today)}</span>
          </div>
          <h2 style={styles.heroTitle}>Bugünden bir cümle kalsın.</h2>
        </section>

        {(!todayMyNote || editing) ? (
          <div style={styles.writeCard}>
          <div style={styles.cardTop}>
            <p style={styles.cardLabel}>Senin cümlen</p>
          </div>
            <div style={{ position: 'relative' }}>
              <AnimatePresence>
                {flyAnim && (
                  <motion.div
                    initial={{ y: 0, x: 0, opacity: 1, rotate: 0 }}
                    animate={{ y: -120, x: 40, opacity: 0, rotate: 15 }}
                    exit={{}}
                    transition={{ duration: 0.65, ease: 'easeOut' }}
                    style={styles.flyNote}
                  >
                    ✎
                  </motion.div>
                )}
              </AnimatePresence>
              <textarea
                placeholder="Bugünden kalan tek cümle..."
                value={text}
                onChange={e => setText(e.target.value)}
                rows={4}
                style={styles.textarea}
              />
            </div>
            <div style={styles.writeActions}>
              {editing && (
                <button type="button" style={styles.cancelBtn} onClick={cancelEdit}>
                  Vazgeç
                </button>
              )}
              <motion.button whileTap={{ scale: 0.96 }} className="btn btn-primary" onClick={save} disabled={saving}>
                {saving ? 'Kaydediliyor...' : editing ? 'Güncelle' : 'Kaydet'}
              </motion.button>
            </div>
          </div>
        ) : null}

        <section style={styles.todayBoard}>
          <div style={styles.cardTop}>
            <p style={styles.cardLabel}>İkimizin notu</p>
          </div>

          <div style={styles.todayGrid}>
            <div style={styles.todayTile}>
              <div style={styles.noteHeader}>
                <span style={styles.noteName}>{user.displayName}</span>
              </div>
              {todayMyNote ? (
                <>
                  <p style={styles.noteText}>{todayMyNote.text}</p>
                  <button type="button" style={styles.editBtn} onClick={startEdit}>Düzenle</button>
                </>
              ) : (
                <p style={styles.emptyNote}>Henüz yazmadın.</p>
              )}
            </div>

            <div style={styles.todayTile}>
              <div style={styles.noteHeader}>
                <span style={styles.noteName}>{todayPartnerNote?.userName || partner}</span>
              </div>
              {todayPartnerNote ? (
                <p style={styles.noteText}>{todayPartnerNote.text}</p>
              ) : (
                <p style={styles.emptyNote}>Henüz yazmadı.</p>
              )}
            </div>
          </div>
        </section>

        {Object.entries(grouped).length > 0 && (
          <section style={styles.historyBlock}>
            <p style={styles.sectionTitle}>Daha Önce</p>
            {Object.entries(grouped).map(([date, dateNotes], gi) => (
              <motion.div
                key={date}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: gi * 0.04, duration: 0.3, ease: 'easeOut' }}
                style={styles.pastDay}
              >
                <p style={styles.dateLabel}>{formatDate(date)}</p>
                <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                  {dateNotes.map((note) => (
                    <div key={note.id} style={styles.pastNote}>
                      <div style={styles.noteHeader}>
                        <span style={styles.noteName}>{note.userName}</span>
                        <span style={styles.noteHeart}>{note.userId === user.uid ? 'Sen' : partner}</span>
                      </div>
                      <p style={styles.pastNoteText}>{note.text}</p>
                    </div>
                  ))}
                </div>
              </motion.div>
            ))}
          </section>
        )}

      </div>
    </div>
  );
}

const styles: Record<string, React.CSSProperties> = {
  title: { fontFamily: "'Inter',sans-serif", fontSize: 22, fontWeight: 850, letterSpacing: '-0.03em' },
  body: { padding: '0 16px 28px', display: 'flex', flexDirection: 'column', gap: 14 },
  heroCard: {
    background: 'linear-gradient(145deg, rgba(191,90,242,0.16), rgba(255,255,255,0.045))',
    border: '1px solid rgba(255,255,255,0.1)',
    borderRadius: 30,
    padding: '20px 18px',
    boxShadow: '0 24px 80px rgba(0,0,0,0.28), inset 0 1px 0 rgba(255,255,255,0.08)',
    backdropFilter: 'blur(22px) saturate(180%)',
  },
  heroMeta: {
    display: 'flex',
    gap: 8,
    alignItems: 'center',
    color: 'rgba(255,255,255,0.52)',
    fontSize: 12,
    fontWeight: 850,
    marginBottom: 12,
  },
  heroTitle: {
    color: 'white',
    fontSize: 25,
    lineHeight: 1.1,
    fontWeight: 900,
    letterSpacing: '-0.05em',
    marginBottom: 14,
  },
  heroPills: { display: 'flex', gap: 8, flexWrap: 'wrap' },
  flyNote: {
    position: 'absolute',
    inset: 0,
    background: 'rgba(255,255,255,0.08)',
    border: '1px solid rgba(255,255,255,0.1)',
    borderRadius: 18,
    zIndex: 2,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    fontSize: 28,
    color: 'rgba(255,255,255,0.8)',
    backdropFilter: 'blur(14px)',
  },
  cardTop: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 12, marginBottom: 12 },
  cardLabel: { fontSize: 13, fontWeight: 850, color: 'rgba(255,255,255,0.68)', margin: 0 },
  donePill: {
    borderRadius: 999,
    padding: '6px 9px',
    background: 'rgba(125,255,143,0.1)',
    border: '1px solid rgba(125,255,143,0.18)',
    color: 'rgba(200,255,210,0.82)',
    fontSize: 11,
    fontWeight: 850,
  },
  waitPill: {
    borderRadius: 999,
    padding: '6px 9px',
    background: 'rgba(255,255,255,0.055)',
    border: '1px solid rgba(255,255,255,0.08)',
    color: 'rgba(255,255,255,0.42)',
    fontSize: 11,
    fontWeight: 850,
  },
  writeCard: {
    background: 'rgba(255,255,255,0.055)',
    border: '1px solid rgba(255,255,255,0.085)',
    borderRadius: 26,
    padding: 16,
    display: 'flex', flexDirection: 'column', gap: 12,
    backdropFilter: 'blur(20px) saturate(180%)',
  },
  textarea: { marginBottom: 10, minHeight: 128, borderRadius: 18 },
  writeActions: { display: 'flex', flexDirection: 'column', gap: 10 },
  cancelBtn: {
    width: '100%',
    border: '1px solid rgba(255,255,255,0.09)',
    background: 'rgba(255,255,255,0.055)',
    color: 'rgba(255,255,255,0.68)',
    borderRadius: 999,
    padding: '13px 16px',
    fontSize: 14,
    fontWeight: 800,
    cursor: 'pointer',
  },
  writeHeader: { display: 'flex', gap: 8, alignItems: 'center', color: 'rgba(255,255,255,0.7)', fontSize: 15 },
  writeIcon: {
    width: 32, height: 32, borderRadius: 12, display: 'grid', placeItems: 'center',
    background: 'rgba(255,255,255,0.08)', border: '1px solid rgba(255,255,255,0.1)', fontWeight: 900,
  },
  todayBoard: {
    background: 'rgba(255,255,255,0.055)',
    border: '1px solid rgba(255,255,255,0.085)',
    borderRadius: 26,
    padding: 16,
    backdropFilter: 'blur(20px) saturate(180%)',
  },
  todayGrid: { display: 'grid', gridTemplateColumns: '1fr', gap: 10 },
  todayTile: {
    borderRadius: 20,
    padding: '14px 15px',
    background: 'rgba(255,255,255,0.045)',
    border: '1px solid rgba(255,255,255,0.07)',
  },
  emptyNote: { color: 'rgba(255,255,255,0.34)', fontSize: 14, lineHeight: 1.5 },
  historyBlock: { marginTop: 4 },
  sectionTitle: { fontSize: 12, fontWeight: 850, color: 'rgba(255,255,255,0.5)', textTransform: 'uppercase', letterSpacing: 1, paddingLeft: 4, marginBottom: 10 },
  pastDay: { marginBottom: 14 },
  dateLabel: { fontSize: 12, color: 'rgba(255,255,255,0.4)', textTransform: 'uppercase', letterSpacing: 1, marginBottom: 8, paddingLeft: 4 },
  pastNote: {
    padding: 14,
    borderRadius: 20,
    background: 'rgba(255,255,255,0.04)',
    border: '1px solid rgba(255,255,255,0.075)',
  },
  noteCard: {
    padding: 14, borderRadius: 14,
    borderLeft: '3px solid',
  },
  noteHeader: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 6 },
  noteName: { fontSize: 12, fontWeight: 850, color: 'rgba(255,255,255,0.68)', letterSpacing: 0.2 },
  noteHeart: { fontSize: 10, color: 'rgba(255,255,255,0.36)', fontWeight: 800, textTransform: 'uppercase' },
  noteText: { fontSize: 15, lineHeight: 1.58, color: 'rgba(255,255,255,0.84)' },
  editBtn: { marginTop: 10, padding: 0, border: 'none', background: 'transparent', color: 'rgba(191,90,242,0.9)', fontSize: 12, fontWeight: 850, cursor: 'pointer' },
  pastNoteText: { fontSize: 14, lineHeight: 1.55, color: 'rgba(255,255,255,0.82)' },
  emptyIcon: { fontSize: 52, color: 'rgba(255,255,255,0.24)', fontWeight: 900 },
  empty: {
    display: 'flex', flexDirection: 'column', alignItems: 'center',
    gap: 8, padding: '60px 20px', color: 'rgba(255,255,255,0.5)', fontSize: 15, textAlign: 'center',
  },
};
